import { describeKnowledgeApiError } from 'utils/knowledgeErrors';
import { message } from 'antd';
import { useRequest } from 'ahooks';
import { useMemo } from 'react';
import { noop, map, filter as _filter, some } from 'lodash-es';
import {
  createCustomClass,
  getDomainList,
  bindOfficialClass,
  updateCustomClass,
} from '@knowledge/service/api/single/manage';
import { useTranslate } from '@tolgee/react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { useMutation, useQuery } from '@tanstack/react-query';
import { mergeItems } from '@knowledge/common/helpers/sku';
import { getSpuClass } from '@knowledge/service/api/single';

let refreshFn = noop;

export const useSingleClassMutate = () => {
  const { t } = useTranslate();

  const { runAsync: create, loading: createLoading } = useRequest(createCustomClass, {
    manual: true,
    onSuccess: () => {
      message.success(t('operation.saveSucceed'));
      refreshFn();
    },
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('operation.saveFailed')),
      );
    },
  });

  const { runAsync: bindIt, loading: bindLoading } = useRequest(bindOfficialClass, {
    manual: true,
    onSuccess: () => {
      refreshFn();
    },
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('operation.saveFailed')),
      );
    },
  });

  const { runAsync: update, loading: updateLoading } = useRequest(updateCustomClass, {
    manual: true,
    onSuccess: () => {
      refreshFn();
    },
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('operation.saveFailed')),
      );
    },
  });

  return {
    create,
    bindIt,
    update,
    loading: createLoading || bindLoading || updateLoading,
  };
};

const getFromNameMultiLang = (source: Record<string, string>, lang: string, fallback?: string) => {
  if (lang === 'zh-CN') lang = 'cn';
  if (lang === 'en-US') lang = 'en';
  if (lang === 'vi') lang = 'vn';
  if (lang === 'tl') lang = 'ph';
  return source[lang?.toUpperCase()] || fallback;
};

const safeParse = (str: string): Record<string, string> => {
  try {
    return JSON.parse(str);
  } catch (e) {
    return {};
  }
};

export type TreeNodes = {
  value: number | string;
  title: string;
  selectable?: boolean;
  children?: TreeNodes;
}[];
export const useDomainList = (
  filter: boolean = false,
  excludeListInput: Knowledge.SingleClass.Value[] = [],
) => {
  const { t } = useTranslate();
  const { data: excludeList } = useQuery({
    queryKey: ['getSpuClassList'],
    queryFn: () => getSpuClass(),
    staleTime: 1000 * 60 * 60,
  });
  const { data, loading } = useRequest(getDomainList, {
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('phrase.获取数据失败，请稍后再试')),
      );
    },
  });
  const parseLangData = useMemo(() => {
    return data?.map((item) => {
      return {
        ...item,
        spuClass: item.spuClass.map((spuClass) => {
          return {
            ...spuClass,
            multiLang: safeParse(spuClass.nameMultiLang),
          };
        }),
      };
    });
  }, [data]);
  const lang = useSystemLanguageValue();
  const actualExcludeList = excludeListInput.length ? excludeListInput : excludeList;
  const domainList = useMemo(() => {
    return _filter(
      map(parseLangData, (item) => {
        return {
          value: item.domain,
          title: item.domain ? t(`ViewDashboard.${item.domain}`) : '',
          children: map(
            filter
              ? _filter(item.spuClass, (e) => {
                  return !some(actualExcludeList, (x) => x.id === e.id);
                })
              : item.spuClass,
            (spuClass) => {
              return {
                value: spuClass.id,
                title: getFromNameMultiLang(spuClass.multiLang, lang, spuClass.name),
                selectable: true,
              };
            },
          ),
          selectable: false,
        };
      }) as TreeNodes,
      (e) => {
        return e.children?.length;
      },
    ) as TreeNodes;
  }, [filter, parseLangData, actualExcludeList, lang, t]);

  return {
    data: domainList,
    loading,
  };
};

/**
 * 新的编辑hook
 */
export const useUpdateSingleClass = () => {
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (data: {
      singleClass: Knowledge.SingleClass.Value;
      params: {
        items: ItemSelectV2.Item[];
        name?: string;
      };
    }) => {
      const { singleClass, params } = data;
      const itemsA = map(singleClass.spus, (spu) => {
        const { itemInternalId, skus, spuId, platform, region } = spu;
        return {
          spuId,
          platform,
          region,
          itemInternalId,
          skus: map(skus, (sku) => ({ skuId: sku.skuId })),
        };
      });
      const itemB = map(params.items, (item) => {
        const { externalItemId, internalItemId, skuInfo, platform, region } = item;
        return {
          spuId: externalItemId,
          platform,
          region,
          itemInternalId: internalItemId,
          skus: map(skuInfo, (sku) => ({ skuId: sku.skuId })),
        };
      });
      const items = mergeItems(itemsA, itemB);
      if (singleClass.source === 'OFFICIAL') {
        return bindOfficialClass({
          spuClassId: singleClass.id,
          items,
        });
      } else {
        return updateCustomClass({
          spuClassId: singleClass.id,
          name: params.name || singleClass.name,
          spus: items,
        });
      }
    },
  });

  return {
    mutateAsync,
    isLoading,
  };
};

export const useCreateSingleClass = () => {
  const { t } = useTranslate();
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (params: { type: 'CUSTOM' | 'OFFICIAL'; name: string; spuClassId: number }) => {
      if (params.type === 'OFFICIAL') {
        return bindOfficialClass({
          spuClassId: params.spuClassId,
          items: [],
        }) as any;
      } else {
        return createCustomClass({
          name: params.name,
          spus: [],
        });
      }
    },
    onError: (knowledgeError) => {
      message.error(
        describeKnowledgeApiError(knowledgeError, undefined, t('phrase.保存失败，请稍后再试')),
      );
    },
  });

  return {
    mutateAsync,
    isLoading,
  };
};
